import React, { useEffect, useRef, useState, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { Icon } from '@appd/uik';

/**
 * For internal use only - not meant for external facing customers yet.
 */

interface CodePopoverProps {
  buttonLabel: string;
  title: string;
  isStringifiedCode: boolean;
  code: string;
}

interface PopoverPortalProps {
  children: ReactNode;
}

const PopoverPortal: React.FC<PopoverPortalProps> = ({ children }) => {
  return ReactDOM.createPortal(children, document.getElementById('fso-dashify') as HTMLElement);
};

export const CodePopover: React.FC<CodePopoverProps> = ({
  buttonLabel = 'json',
  title = 'json',
  code,
  isStringifiedCode = false,
}) => {
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const codeSectionRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setPopoverOpen(false);
        setIsCopied(false); // Reset copied state when closing the popover
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  const handleCopyClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (codeSectionRef.current) {
      const range = document.createRange();
      range.selectNode(codeSectionRef.current);

      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);

        document.execCommand('copy');
        selection.removeAllRanges();
        setIsCopied(true); // Set copied state to true
      }
    }
  };

  const jsonCode = isStringifiedCode ? JSON.parse(code) : code;
  return (
    <div className="absolute right-[12px] top-[12px] inline-block" ref={buttonRef}>
      <button
        className="flex items-center bg-default-bg-strong-disabled text-gray-800 rounded focus:outline-none focus:ring-1 focus:interact-text-focus focus:interact-border-medium-focus focus:ring-offset-1 focus:ring-offset-transparent"
        style={{ padding: '3px' }}
        onClick={() => {
          setPopoverOpen(!isPopoverOpen);
          setIsCopied(false); // Reset copied state when opening the popover
        }}
      >
        <div className="flex items-center bg-default-bg-strong-disabled">
          <Icon size="SMALL" type="Widget.List" className="mr-2" />
          <span className="dark:text-gray-300" style={{ fontSize: '11px', marginLeft: '2px' }}>
            {isCopied ? 'Copied!' : buttonLabel}
          </span>
        </div>
      </button>

      {isPopoverOpen && (
        <PopoverPortal>
          <div className="fixed z-50 top-[16px] right-0 w-[50%] max-h-[100%] bg-brand-bg-active text-inverse-text border-2 rounded-lg border-interact-border-disabled shadow-md p-4 mt-2 max-h-full overflow-y-auto">
            <div className="flex items-center justify-between mb-2 mx-[-16px] mt-[-16px] bg-brand-bg-hover p-2 rounded">
              <span className="font-medium text-md">{title}</span>
              <button
                className="bg-default-bg-disabled text-gray-900 p-[3px] rounded focus:outline-none hover:bg-blue-600"
                onClick={handleCopyClick}
              >
                <div className="flex items-center">
                  <Icon size="SMALL" type={isCopied ? 'Action.Enable' : 'Action.Copy'} className="mr-1" />
                  <span>{isCopied ? 'Copied!' : 'Copy Code'}</span>
                </div>
              </button>
            </div>
            <pre ref={codeSectionRef} className="code-section">
              {JSON.stringify(jsonCode, null, 2)} {/* Format JSON with indentation */}
            </pre>
          </div>
        </PopoverPortal>
      )}
    </div>
  );
};
